// src/components/fix-actions/redesign/preview.ts
//
// DEV-ONLY preview data for the Action Items redesign. Lets you eyeball how the
// Optimization Studio and the Deploy log render without depending on the local
// backend actually generating assets. Purely for local viewing - never affects
// real data and is off by default.
//
// Enable (build-time only, never in client-facing builds):
//   env          VITE_ACTION_ITEMS_PREVIEW=1
//
// With the env var set, ?actionItemsPreview=0 turns it off for a visit.

import type { StrategyArtifact } from "../../../services/fix-actions/optimization-studio-service";
import type { DeployLogEntry, DeployStatus } from "../../../services/fix-actions/fix-actions-service";

function truthy(v: string | null | undefined): boolean {
  const s = String(v ?? "").trim().toLowerCase();
  return s === "1" || s === "true";
}
function falsy(v: string | null | undefined): boolean {
  const s = String(v ?? "").trim().toLowerCase();
  return s === "0" || s === "false";
}

function readFlag(): boolean {
  try {
    // Preview data is gated on the build-time env var ONLY. URL params and
    // localStorage must never be able to turn demo data on for a client.
    const env = (import.meta as any)?.env ?? {};
    if (!truthy(env.VITE_ACTION_ITEMS_PREVIEW)) return false;
    if (typeof window !== "undefined") {
      const q = new URLSearchParams(window.location.search).get("actionItemsPreview");
      if (q !== null && falsy(q)) return false;
    }
    return true;
  } catch {
    return false;
  }
}

export const ACTION_ITEMS_PREVIEW: boolean = readFlag();

function isoAgo(hours: number): string {
  return new Date(Date.now() - hours * 3600_000).toISOString();
}

interface Seed {
  id: string;
  name: string;
  type: string;
  actionId: number;
  actionTitle: string;
  theme: string;
  status: string; // ArtifactStatus-ish
  deployStatus: DeployStatus;
  confidence: number;
  targets: string[];
  intent: string;
  reasoning: string;
  ageH: number;
}

const SEEDS: Seed[] = [
  {
    id: "prev-1", name: "Homepage Entity Schema Package", type: "Structured Data / Schema",
    actionId: 1, actionTitle: "Clarify Iterate entity signals on the homepage", theme: "Brand disambiguation",
    status: "Ready", deployStatus: "Ready", confidence: 93, targets: ["Owned site", "Structured Data"],
    intent: "Make the homepage a clear entity source of truth so models stop conflating Iterate with similarly named brands.",
    reasoning: "Organization + sameAs schema gives crawlers unambiguous identity signals to attach to the right entity.", ageH: 3,
  },
  {
    id: "prev-2", name: "Brand Disambiguation Block", type: "Boundary / Trust Artifact",
    actionId: 1, actionTitle: "Clarify Iterate entity signals on the homepage", theme: "Brand disambiguation",
    status: "In Review", deployStatus: "In Review", confidence: 88, targets: ["Owned site"],
    intent: "State plainly what Iterate is and is not, so models stop inheriting attributes from unrelated brands.",
    reasoning: "Explicit boundary language reduces fabricated or cross-brand claims in model answers.", ageH: 6,
  },
  {
    id: "prev-3", name: "Social Proof Hero Copy", type: "Voice & Messaging",
    actionId: 2, actionTitle: "Add homepage social proof above the fold", theme: "Authority building",
    status: "Draft", deployStatus: "Draft", confidence: 79, targets: ["Owned site"],
    intent: "Lead with verified customer and backer signals so authority is the first thing models and prospects read.",
    reasoning: "Above-the-fold, repeated proof becomes the phrasing models reach for when describing credibility.", ageH: 20,
  },
  {
    id: "prev-4", name: "Pricing Tiers FAQ", type: "FAQ / Knowledge Base",
    actionId: 3, actionTitle: "Publish AI-readable pricing tiers", theme: "Pricing clarity",
    status: "Deployed", deployStatus: "Published", confidence: 95, targets: ["Owned site", "Structured Data"],
    intent: "Give models clean, citable answers to pricing questions instead of guessing or hedging.",
    reasoning: "FAQPage markup lets models surface exact pricing answers close to verbatim.", ageH: 30,
  },
  {
    id: "prev-5", name: "Iterate vs. Alternatives Comparison", type: "Comparison Asset",
    actionId: 3, actionTitle: "Publish AI-readable pricing tiers", theme: "Pricing clarity",
    status: "Ready", deployStatus: "Ready", confidence: 90, targets: ["Owned site", "Third-party blogs"],
    intent: "Own the head-to-head framing so comparison prompts cite Iterate's positioning.",
    reasoning: "Models lean on explicit comparison structures for 'X vs Y' and 'alternatives to X' prompts.", ageH: 8,
  },
  {
    id: "prev-6", name: "Evidence & Citation Pack", type: "Evidence & Citation Pack",
    actionId: 2, actionTitle: "Add homepage social proof above the fold", theme: "Authority building",
    status: "Deployed", deployStatus: "Published", confidence: 96, targets: ["Owned site"],
    intent: "Turn scattered proof into structured, attributable evidence models can cite.",
    reasoning: "Sourced, extractable proof reduces hedging in answers about credibility and outcomes.", ageH: 48,
  },
];

export function previewArtifacts(brandName?: string): StrategyArtifact[] {
  return SEEDS.map((s) => ({
    id: s.id,
    name: s.name,
    type: s.type as any,
    supportingActionId: s.actionId,
    supportingActionTitle: s.actionTitle,
    strategicTheme: s.theme,
    status: s.status as any,
    confidence: s.confidence,
    deploymentTargets: s.targets,
    generatedAt: isoAgo(s.ageH),
    lastUpdated: isoAgo(Math.max(0, s.ageH - 1)),
    intent: s.intent,
    aiReasoningBehavior: s.reasoning,
    relationshipToAction: `Supports "${s.actionTitle}" for ${brandName || "your brand"}.`,
    structurePreview: { type: "list", content: [] },
    fullContent: {
      type: "generic",
      summary: s.intent,
      body: `${s.reasoning} Deployment: ${s.targets.join(", ")}.`,
    },
  }));
}

export function previewDeployLog(): DeployLogEntry[] {
  return SEEDS.map((s, i) => ({
    id: 9000 + i,
    artifactId: s.id,
    actionId: s.actionId,
    artifactName: s.name,
    artifactType: s.type,
    status: s.deployStatus,
    createdAt: isoAgo(s.ageH),
    updatedAt: isoAgo(Math.max(0, s.ageH - 1)),
  }));
}
